import { useState } from 'react';
import { IconCheck, IconPhone, IconPlus, IconTruck, IconTruckDelivery } from '@tabler/icons-react';
import { Button, Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import { ASSIGN_OPTS, PHONE_FILTERS, PHONE_TRIPS, phoneCounts, phoneFilter, phoneStatus, phoneSteps, type PhoneFilter, type PhoneTrip } from './phone';
import { useT } from '@hana/i18n';
import './i18n';
import s from './phone.module.css';

// Mint-TMS-Phone · khung 1 (390): chip lọc, thẻ P0, thẻ chuyến + Gán / Đổi xe. Chỉ đọc; gán xe giữ trong bộ nhớ, toast có Hoàn tác.
// Khung 2 "Lịch trực của tôi" ở DutyPhone.tsx (/ezy/tms/duty). Chưa làm: ẩn / hiện thanh khi cuộn, nút đổi giao diện (ở khung máy của board, không thuộc màn).

const STEP_CLS = { done: 'done', cur: 'cur', 'cur-late': 'curLate', 'cur-end': 'curEnd', next: '' } as const;

export function PhoneCreate() {
  const toast = useToast();
  return (
    <button type="button" className={s.create} aria-label="Tạo chuyến" onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
      <IconPlus size={22} aria-hidden="true" />
    </button>
  );
}

function Card({ t, truck, onAssign }: { t: PhoneTrip; truck: string; onAssign: () => void }) {
  const st = phoneStatus(t, truck !== t.truck);
  const late = t.risk === 'late' || t.risk === 'gấp';
  return (
    <div className={s.card}>
      <div className={s.cardHd}>
        <b>{`${truck} · ${t.id}`}</b>
        {t.flag && <span className={`${s.flag} ${t.risk === 'ok' ? s.flagOk : t.risk === 'late' ? s.flagLate : s.flagErr}`}>{t.flag}</span>}
        <span className={`${s.due} ${late ? s.dueErr : ''}`}>{t.due}</span>
      </div>
      <div className={s.desc}>{t.desc}</div>
      <div className={s.steps} aria-hidden="true">
        {phoneSteps(t).map((k, j) => <span key={j} className={s[STEP_CLS[k]]} />)}
      </div>
      <div className={s.foot}>
        <span className={`${s.st} ${st.unassigned ? s.stWarn : ''}`}>{st.text}</span>
        <button type="button" className={s.act} onClick={onAssign}>
          <IconTruck size={16} aria-hidden="true" />
          {st.action}
        </button>
      </div>
    </div>
  );
}

export function PhoneToday({ state, onRetry }: { state: ScreenState; onRetry: () => void }) {
  const toast = useToast();
  const t = useT();
  const [f, setF] = useState<PhoneFilter>('all');
  const [p0Done, setP0Done] = useState(false);
  const [over, setOver] = useState<Record<string, string>>({});
  const [asg, setAsg] = useState<string | null>(null);
  const counts = phoneCounts(PHONE_TRIPS);
  const rows = phoneFilter(PHONE_TRIPS, f);
  const noperm = state === 'noperm';

  const pick = (truck: string) => {
    const id = asg;
    if (!id) return;
    const prev = over;
    setOver({ ...over, [id]: truck });
    setAsg(null);
    toast({ msg: `Đã gán ${truck}`, undo: () => setOver(prev) });
  };

  return (
    <div className={s.wrap}>
      <div className={s.seg} role="tablist" aria-label="Lọc chuyến">
        {PHONE_FILTERS.map((x) => (
          <button key={x.key} type="button" role="tab" aria-selected={f === x.key} onClick={() => setF(x.key)}>
            {`${x.label} ${counts[x.key]}`}
          </button>
        ))}
      </div>
      <StateView
        state={state}
        icon={<IconTruckDelivery size={26} stroke={1.8} />}
        title={state === 'empty' ? 'Chưa có chuyến hôm nay' : undefined}
        action={state === 'error' ? <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined}
        skeleton={[1, 2, 3, 4, 5].map((k) => <div key={k} className={s.skel}><i style={{ width: '55%' }} /><i style={{ width: '80%' }} /><i style={{ width: '100%', height: 6, marginTop: 18 }} /></div>)}
      >
        {!p0Done && (
          <div role="alert" className={s.p0}>
            <div className={s.p0Hd}>
              <span className={s.p0Badge}>P0</span>
              <b>{t('tms.reeferPower')}</b>
              <span className={s.p0Esc}>4′</span>
            </div>
            <div className={s.p0Tx}>{t('tms.reeferDetail')}</div>
            <div className={s.p0Act}>
              <a href="tel:0909000315">
                <IconPhone size={16} aria-hidden="true" />
                {t('tms.callDriver')}
              </a>
              <button type="button" onClick={() => setP0Done(true)}>
                <IconCheck size={16} aria-hidden="true" />
                {t('tms.iHandle')}
              </button>
            </div>
          </div>
        )}
        <div className={s.wrap} style={{ padding: 0 }}>
          {rows.map((t) => (
            <Card key={t.id} t={t} truck={over[t.id] ?? t.truck} onAssign={() => (noperm ? toast({ msg: 'Chỉ xem: không có quyền gán xe' }) : setAsg(t.id))} />
          ))}
        </div>
        {noperm && <div className={s.view}>{t('tms.viewOnlyAssign')}</div>}
      </StateView>
      <Modal open={asg !== null} onOpenChange={(o) => !o && setAsg(null)} title={asg ? `Gán xe · ${asg}` : 'Gán xe'}>
        {ASSIGN_OPTS.map((o) => (
          <button key={o.k} type="button" className={s.opt} onClick={() => pick(o.k)}>
            <span className={`${s.optIc} ${o.kind === 'XN' ? s.optXn : s.optNx}`}>{o.kind}</span>
            <span className={s.optTx}>
              <div>{o.k}</div>
              <div>{o.v}</div>
            </span>
          </button>
        ))}
      </Modal>
    </div>
  );
}
